import React from 'react';

/**
 * SamSpinner — branded waiting spinner (blue + yellow arcs).
 *
 * Props:
 *   size      — pixel diameter (default 56)
 *   text      — status label below spinner (default 'Working')
 *   progress  — optional "3/100" progress string
 *   dark      — if true, hide-arc matches dark background (default true)
 */
export default function SamSpinner({ size = 56, text = 'Working', progress, dark = true }) {
    const bgColor = dark ? '#1E293B' : 'var(--color-bg-base)';
    const strokeWidth = size <= 36 ? 9 : size <= 60 ? 8 : 7;

    return (
        <div style={S.wrap}>
            <div style={{ position: 'relative', width: size, height: size }}>
                {/* Pulse glow */}
                <div style={{
                    ...S.glow,
                    width: size * 0.75,
                    height: size * 0.75,
                }} />
                {/* Rotating ring */}
                <svg
                    style={{ animation: 'samSpinnerSpin 1.5s cubic-bezier(0.4,0,0.2,1) infinite' }}
                    width={size} height={size}
                    viewBox="0 0 100 100"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle cx="50" cy="50" r="34"
                        fill="none" stroke="#5B9FFF" strokeWidth={strokeWidth}
                        strokeDasharray="58 155.6" strokeDashoffset="55"
                        strokeLinecap="round" />
                    <circle cx="50" cy="50" r="34"
                        fill="none" stroke="#FFC107" strokeWidth={strokeWidth}
                        strokeDasharray="58 155.6" strokeDashoffset="-16"
                        strokeLinecap="round" />
                    <circle cx="50" cy="50" r="34"
                        fill="none" stroke={bgColor} strokeWidth={strokeWidth}
                        strokeDasharray="58 155.6" strokeDashoffset="-87"
                        strokeLinecap="round" />
                </svg>
            </div>
            {text && <span style={S.text}>{text}</span>}
            {progress && <span style={S.progress}>{progress}</span>}
            {/* Keyframes injected once */}
            <style>{`
                @keyframes samSpinnerSpin {
                    0%   { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @keyframes samSpinnerPulse {
                    0%, 100% { transform: translate(-50%,-50%) scale(1);   opacity: 0.6; }
                    50%      { transform: translate(-50%,-50%) scale(1.35); opacity: 1;   }
                }
                @keyframes samSpinnerFade {
                    0%, 100% { opacity: 0.5; }
                    50%      { opacity: 1;   }
                }
            `}</style>
        </div>
    );
}

const S = {
    wrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
    },
    glow: {
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(91,159,255,0.12) 0%, transparent 70%)',
        animation: 'samSpinnerPulse 2.4s ease-in-out infinite',
    },
    text: {
        fontSize: 12,
        fontWeight: 400,
        letterSpacing: 2,
        textTransform: 'uppercase',
        color: '#8C9BAA',
        animation: 'samSpinnerFade 2.4s ease-in-out infinite',
    },
    progress: {
        fontSize: 13,
        fontWeight: 600,
        color: '#CBD5E1',
        fontVariantNumeric: 'tabular-nums',
    },
};
